import { useTranslation } from 'react-i18next';

import type { OwnershipEventActivity, TaskEventActivity } from './types';

export function TaskEventMessage({
  activity,
}: {
  activity: TaskEventActivity;
}) {
  const { t } = useTranslation('tasks');

  return (
    <p className="text-center text-xs text-muted-foreground">
      {getTaskEventLabel(activity, t)}
    </p>
  );
}

export function getTaskEventLabel(
  activity: TaskEventActivity,
  t: ReturnType<typeof useTranslation<'tasks'>>['t'],
) {
  const { event, actorName, assigneeName } = activity;

  if (event === 'created') {
    return actorName
      ? t('Task created by {{name}}', { name: actorName })
      : t('Task created');
  }

  if (event === 'completed') {
    return actorName
      ? t('Task completed by {{name}}', { name: actorName })
      : t('Task completed');
  }

  if (event === 'reopened') {
    return actorName
      ? t('Task reopened by {{name}}', { name: actorName })
      : t('Task reopened');
  }

  if (!assigneeName) {
    return actorName
      ? t('{{actor}} unassigned the task', { actor: actorName })
      : t('Task unassigned');
  }

  if (!actorName) {
    return t('Assigned to {{name}}', { name: assigneeName });
  }

  if (actorName === assigneeName) {
    return t('{{name}} took the task', { name: actorName });
  }

  return t('{{actor}} assigned {{assignee}}', {
    actor: actorName,
    assignee: assigneeName,
  });
}

/**
 * Feed chrome: a channel, or this enquiry, changed hands.
 *
 * Without it, history shows up in a feed dated weeks ago with nothing
 * accounting for how it got there.
 */
export function OwnershipEventMessage({
  activity,
}: {
  activity: OwnershipEventActivity;
}) {
  const { t } = useTranslation('tasks');

  return (
    <p className="text-center text-xs text-muted-foreground">
      {getOwnershipEventLabel(activity, t)}
    </p>
  );
}

export function getOwnershipEventLabel(
  activity: OwnershipEventActivity,
  t: ReturnType<typeof useTranslation<'tasks'>>['t'],
) {
  const { event, subject, counterpartName, actorName } = activity;
  const what =
    subject.kind === 'phone'
      ? subject.phoneNumber
      : subject.kind === 'email'
        ? subject.email
        : t('this enquiry');
  const by = actorName ?? null;

  if (event === 'gained') {
    if (counterpartName) {
      return by
        ? t('{{actor}} moved {{what}} here from {{other}}', {
            actor: by,
            what,
            other: counterpartName,
          })
        : t('{{what}} moved here from {{other}}', {
            what,
            other: counterpartName,
          });
    }

    return by
      ? t('{{actor}} attached {{what}}', { actor: by, what })
      : t('{{what}} attached', { what });
  }

  if (counterpartName) {
    return by
      ? t('{{actor}} moved {{what}} to {{other}}', {
          actor: by,
          what,
          other: counterpartName,
        })
      : t('{{what}} moved to {{other}}', { what, other: counterpartName });
  }

  return by
    ? t('{{actor}} detached {{what}}', { actor: by, what })
    : t('{{what}} detached', { what });
}
